/**
 * TODO - preamble
 */
import React from "react";
import { FormattedHTMLMessage, type InjectedIntl, injectIntl } from "react-intl";

import { getSafe } from "@jslib/shared/utils/getSafe";

import { useCollectFieldsEndpoint } from "@/lib/ServerApi/useCollectFieldsEndpoint";

import { VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import {
  type VerificationService,
  type AgePersonalInfoViewModel,
  type AgePersonalInfoResponse,
  type FormSelectChoice,
  type Country,
  type FieldDescription,
  type CustomFormFieldConfig,
} from "../../lib/types/types";
import {
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
  getAvailableCountryChoices,
  getDefaultCountryChoice,
  produceDraftViewModel,
  getFieldConfigByFieldId,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { PostalCodeComponent as PostalCode } from "../FormFields/PostalCode/PostalCodeComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { CityComponent } from "../FormFields/City/City";
import { AddressComponent } from "../FormFields/Address/AddressComponent";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";

interface StepAgePersonalInfoComponentProps {
  intl: InjectedIntl;
  verificationService: VerificationService;
}

const StepAgePersonalInfo = ({ verificationService, intl }: StepAgePersonalInfoComponentProps) => {
  const viewModel = verificationService.viewModel as AgePersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse = verificationService.verificationResponse as AgePersonalInfoResponse;
  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
  const countryChoices: FormSelectChoice<Country, string>[] = getAvailableCountryChoices(
    verificationService.programTheme,
    intl,
  );
  const defaultCountryChoice: FormSelectChoice<Country, string> =
    getDefaultCountryChoice(countryChoices);
  const safeCountryValue = getSafe(() => viewModel.countryChoice.value, defaultCountryChoice.value);
  const defaultRequiredFields: FieldDescription[] =
    safeCountryValue === "US" ? [{ key: "postalCode" }] : [];

  // PhoneNumber is required for all non-US age programs and if smsLoop is enabled.
  const showPhoneNumber =
    safeCountryValue !== "US" ||
    verificationService.programTheme.isSmsNotifierConfigured ||
    verificationService.programTheme.smsLoopEnabled;
  const phoneNumberRequired =
    safeCountryValue !== "US" || verificationService.programTheme.smsLoopEnabled;

  const locale = viewModel.localeChoice.value;
  const options = getOptions();

  if (
    !fieldValidationErrors.phoneNumber &&
    verificationResponse.errorIds &&
    verificationResponse.errorIds.includes("invalidPhoneNumber")
  ) {
    verificationService.updateFieldValidationErrors({
      ...fieldValidationErrors,
      phoneNumber: "invalidPhoneNumber",
    });
  }

  const updateAgeViewModel = (key: keyof AgePersonalInfoViewModel, value: any) => {
    const nextState: AgePersonalInfoViewModel = produceDraftViewModel(viewModel, key, value);
    verificationService.updateViewModel(nextState);
  };

  const { isFieldRequired } = useCollectFieldsEndpoint({
    verificationId: verificationResponse.verificationId,
    currentStep: verificationResponse.currentStep,
    viewModel,
    defaultRequiredFields,
  });

  React.useEffect(() => {
    // When country changes re-check the phone number validation
    updateFieldValidationErrorsByFieldId("phoneNumber", viewModel.phoneNumber, verificationService);
  }, [viewModel.country]);

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader verificationService={verificationService} classNameIdentifier="age" />

      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("country")?.disabled}
      />

      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          disabled={getFieldConfigByFieldId("firstName")?.disabled}
          onChange={(newValue) => {
            updateAgeViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />

        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          disabled={getFieldConfigByFieldId("lastName")?.disabled}
          onChange={(newValue) => {
            updateAgeViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>
      <BirthDate
        errorId={fieldValidationErrors.birthDate}
        locale={locale}
        onChange={(newValue) => {
          updateAgeViewModel("birthDate", newValue);
          updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
        }}
        value={viewModel.birthDate}
        disabled={getFieldConfigByFieldId("birthDate")?.disabled}
      />
      {isFieldRequired("city") && (
        <CityComponent
          value={viewModel.city}
          isRequired
          isErrored={!!fieldValidationErrors.city}
          errorId={fieldValidationErrors.city}
          disabled={getFieldConfigByFieldId("city")?.disabled}
          onChange={(value) => {
            updateAgeViewModel("city", value);
            updateFieldValidationErrorsByFieldId("city", value, verificationService);
          }}
        />
      )}
      {isFieldRequired("address1") && (
        <AddressComponent
          value={viewModel.address1}
          isRequired
          isErrored={!!fieldValidationErrors.address1}
          errorId={fieldValidationErrors.address1}
          onChange={(value) => {
            updateAgeViewModel("address1", value);
            updateFieldValidationErrorsByFieldId("address1", value, verificationService);
          }}
        />
      )}
      {isFieldRequired("postalCode") && (
        <PostalCode
          isErrored={!!fieldValidationErrors.postalCode}
          onChange={(newValue) => {
            updateAgeViewModel("postalCode", newValue);
            updateFieldValidationErrorsByFieldId("postalCode", newValue, verificationService);
          }}
          value={viewModel.postalCode}
          disabled={getFieldConfigByFieldId("postalCode")?.disabled}
        />
      )}
      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={<FormattedHTMLMessage id="emailExplanation" defaultMessage=" " />}
        onChange={(newValue) => {
          updateAgeViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
        disabled={getFieldConfigByFieldId("email")?.disabled}
      />

      {showPhoneNumber ? (
        <PhoneNumber
          isRequired={phoneNumberRequired}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          onChange={(newValue) => {
            updateAgeViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
          disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
        />
      ) : null}

      {options.customFormFields.map((config: CustomFormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}

      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        onChange={(newValue) => {
          updateAgeViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
        disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
      />

      <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />

      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectAgePersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage
            id="step.personalInfo.submitButtonText"
            defaultMessage="Verify and continue"
          />
        }
      />
    </PersonalInfoWrapper>
  );
};

export const StepAgePersonalInfoComponent = injectIntl(StepAgePersonalInfo);
